import { onUnmounted } from "vue";

export interface PollingHandle {
  start: (tick: () => void | Promise<void>, intervalMs: number) => void;
  stop: () => void;
  isRunning: () => boolean;
}

/**
 * Generic polling helper that ticks a callback on an interval and
 * auto-cleans on component unmount. Each `start()` cancels any prior
 * interval before scheduling a new one, so a component can swap targets
 * (e.g. switching jobs) without leaking timers.
 *
 * The tick is awaited so async errors propagate to the caller's logs
 * rather than being silently dropped; the next tick still fires regardless
 * of the previous one's outcome (polling is resilient).
 */
export function usePolling(): PollingHandle {
  let timer: ReturnType<typeof setInterval> | null = null;

  const stop = (): void => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = (tick: () => void | Promise<void>, intervalMs: number): void => {
    stop();
    timer = setInterval(() => {
      // Catch synchronous throws too (Promise.resolve evaluates tick() first).
      try {
        Promise.resolve(tick()).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("usePolling tick error:", err);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("usePolling tick error:", err);
      }
    }, intervalMs);
  };

  onUnmounted(stop);

  return { start, stop, isRunning: () => timer !== null };
}
